<template>
  <a-layout class="transaction-list">
    <TitleBreadcrumb />
    <div class="content-base container">
      <!-- Alert cancellation requested -->
      <a-modal
        class="requested-cancel-alert-modal"
        :visible="showRequestWithdrawCancellationAlert"
        :title="labels.requestWithdrawCancellationAlert.title"
        :cancelButtonProps="{ style: { display: 'none' } }"
        @ok="handleCloseRequestWithdrawCancellationAlert"
        @cancel="handleCloseRequestWithdrawCancellationAlert"
      >
        <a-alert
          :message="labels.requestWithdrawCancellationAlert.title"
          :description="labels.requestWithdrawCancellationAlert.description"
          type="warning"
          show-icon
        />
      </a-modal>

      <a-card class="transaction-list-card">
        <a-row
          type="flex"
          justify="center"
          :class="{ 'flex-direction-reverse': isMobile }"
          :gutter="[16, 16]"
        >
          <a-col :xs="24" :sm="24" :md="19">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
          <a-col :xs="24" :sm="24" :md="5">
            <router-link :to="{ name: 'transaction-request' }">
              <a-button
                class="btn-new-transaction"
                icon="plus"
                :style="{ width: '100%' }"
              >
                {{ labels.newTransaction }}
              </a-button>
            </router-link>
          </a-col>
        </a-row>
        <a-divider
          orientation="center"
          type="horizontal"
          :style="{ marginBottom: 0 }"
        />
        <a-row type="flex" justify="center" :gutter="[16, 16]">
          <a-col :xs="24">
            <StandardTable
              class="table-base"
              ref="standardTable"
              :fetchService="fetchClientTransactions"
              :columns="columns"
              :locale="locale"
              :handleShowTotal="handleShowTotal"
              :scroll="{ x: isMobile ? true : false }"
              :rowKey="'code'"
              :paginationType="isMobile ? 'arrows' : 'numbers'"
              :customRow="handleCustomRow"
            />
          </a-col>
        </a-row>
      </a-card>
    </div>
  </a-layout>
</template>
<script>
import { mapActions, mapState } from "vuex";
import accounting from "accounting";
import TitleBreadcrumb from "@/components/layout/client/TitleBreadcrumb.vue";
import loadingMixin from "@/mixins/loadingMixin";
import { pageTitle } from "@/utils/utils";
import labels from "@/utils/labels";
import StandardTable from "@/components/core/table/StandardTable.vue";
import { DATE_FORMAT } from "../../../utils/utils";
export default {
  components: { TitleBreadcrumb, StandardTable },
  name: "transactionList",
  mixins: [loadingMixin],
  metaInfo: {
    title: pageTitle(labels.transactionList.subtitle),
    meta: [
      {
        name: "title",
        content: labels.appName,
      },
      {
        name: "description",
        content: labels.transactionList.subtitle,
      },
    ],
  },
  data() {
    return {
      labels: labels.transactionList,
      search: "",
      activeRow: null,
      showRequestWithdrawCancellationAlert: false,
    };
  },
  methods: {
    ...mapActions("transactions", [
      "fetchClientTransactions",
      "requestCancelWithdraw",
      "cancelTransfer",
    ]),
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    confirmCancellation(record) {
      this.$confirm({
        title: this.labels.confirmCancellation.title,
        content: this.labels.confirmCancellation.content.replace(
          "{code}",
          record.code
        ),
        okText: this.labels.confirmCancellation.okText,
        okType: "danger",
        cancelText: this.labels.confirmCancellation.cancelText,
        onOk: () => {
          this.handleCancelTransfer(record);
        },
      });
    },
    async handleCancelTransfer(record) {
      this.loading = true;
      try {
        await this.cancelTransfer(record.code);
        this.$message.success(this.labels.cancelTransferSuccess);
        this.handleReloadTable();
        this.checkSession();
      } catch (error) {
        this.$message.error(this.labels.cancelTransferError);
      } finally {
        this.loading = false;
      }
    },
    confirmRequestWithdrawCancellation(record) {
      this.$confirm({
        title: this.labels.confirmRequestWithdrawCancellation.title,
        class: "requested-cancel-alert-modal",
        content: this.labels.confirmRequestWithdrawCancellation.content.replace(
          "{code}",
          record.code
        ),
        okText: this.labels.confirmRequestWithdrawCancellation.okText,
        okType: "danger",
        cancelText: this.labels.confirmRequestWithdrawCancellation.cancelText,
        onOk: () => {
          this.handleRequestCancelWithdraw(record);
        },
      });
    },
    async handleRequestCancelWithdraw(record) {
      this.loading = true;
      try {
        await this.requestCancelWithdraw(record.code);
        this.$message.success(this.labels.requestCancelWithdrawSuccess);
        this.handleReloadTable();
        this.checkSession();
        this.showRequestWithdrawCancellationAlert = true;
      } catch (error) {
        this.$message.error(this.labels.requestCancelWithdrawError);
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
    handleShowMoreInfo(record, event) {
      event.stopPropagation();
      if (record?.code) {
        this.$info({
          title: this.labels.moreInfo.title.replace(
            "{code}",
            record?.code || "-"
          ),
          class: "more-info-modal",
          content: (
            <div>
              <p>
                <strong>{this.labels.moreInfo.date}:</strong>{" "}
                {this.$moment(record?.date).format(
                  DATE_FORMAT.MOMENT_DATE_TIME
                )}
              </p>
              <p>
                <strong>{this.labels.moreInfo.code}:</strong>{" "}
                {record?.code || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.reference}:</strong>{" "}
                {record?.ticket?.number || record?.reference || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.type}:</strong>{" "}
                {record?.type?.name || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.origin}:</strong>{" "}
                {record?.origin?.name || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.amount}:</strong>{" "}
                {accounting.formatNumber(record?.amount, 2, ",", ".")}
              </p>
              <p>
                <strong>{this.labels.moreInfo.status}:</strong>{" "}
                {this.statusText(record?.status?.toLowerCase())}
              </p>
            </div>
          ),
        });
      }
    },
    handleCloseRequestWithdrawCancellationAlert() {
      this.showRequestWithdrawCancellationAlert = false;
    },
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.date,
          dataIndex: "date",
          key: "date",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return this.$moment(text).format(DATE_FORMAT.MOMENT_DATE_TIME);
          },
        },
        {
          title: this.labels.table.columns.code,
          dataIndex: "code",
          key: "code",
          sorter: true,
          hidden: this.isMobile,
        },
        {
          title: this.labels.table.columns.type,
          dataIndex: "type",
          key: "type",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return text?.name;
          },
        },
        {
          title: this.labels.table.columns.origin,
          dataIndex: "origin",
          key: "origin",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return text?.name;
          },
        },
        {
          title: this.labels.table.columns.amount,
          dataIndex: "amount",
          key: "amount",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return accounting.formatNumber(text || 0, 2, ",", ".");
          },
        },
        {
          title: this.labels.table.columns.status.title,
          dataIndex: "status",
          key: "status",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text, record) => {
            const tooltip = (component) => {
              return (
                <span>
                  {component}
                  <a-tooltip trigger="click" title={record.reject_reason}>
                    <a-icon type="info-circle" class="text-danger" />
                  </a-tooltip>
                </span>
              );
            };
            const status = (
              <a-tag color={this.statusColor(text?.toLowerCase())}>
                {this.statusText(text?.toLowerCase())}
              </a-tag>
            );
            return record?.reject_reason && record?.status === "REJECTED"
              ? tooltip(status)
              : status;
          },
        },
        {
          title: this.labels.table.columns.action.title,
          dataIndex: "action",
          key: "action",
          width: "120px",
          align: "center",
          hidden: this.isMobile,
          customRender: (_, record) => {
            return (
              <div class="action-buttons">
                <router-link
                  to={{
                    name: "transaction-view",
                    params: { code: record.code },
                  }}
                  title={this.labels.table.columns.action.view}
                >
                  <a-button icon="eye" class="action-button-view" />
                </router-link>
                {record?.is_cancellable ||
                  (record?.is_withdraw_cancellable && (
                    <a-divider type="vertical" />
                  ))}
                {record?.is_cancellable ? (
                  <a-button
                    onClick={() => this.confirmCancellation(record)}
                    type="danger"
                    icon="delete"
                  />
                ) : record?.is_withdraw_cancellable ? (
                  <a-button
                    onClick={() =>
                      this.confirmRequestWithdrawCancellation(record)
                    }
                    type="danger"
                    icon="delete"
                  />
                ) : null}

                {record?.requested_withdraw_cancellation &&
                  record?.status === "PENDING" && (
                    <a-tooltip
                      trigger="click"
                      title={this.labels.withdrawCancellationInProgress}
                    >
                      <a-icon
                        type="info-circle"
                        style={{
                          marginLeft: "10px",
                          color: this.statusColor("pending"),
                        }}
                      />
                    </a-tooltip>
                  )}
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.transactions,
          dataIndex: "transactions",
          key: "transactions",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_, record, index) => {
            // Card item design
            const color = this.statusColor(record.status?.toLowerCase());
            const tooltip = (component) => {
              return (
                <span>
                  {component}
                  <a-tooltip
                    trigger="click"
                    onClick={(event) => event.stopPropagation()}
                    title={record.reject_reason}
                  >
                    <a-icon type="info-circle" class="text-danger" />
                  </a-tooltip>
                </span>
              );
            };
            const status = (
              <a-tag color={this.statusColor(record.status?.toLowerCase())}>
                {this.statusText(record.status?.toLowerCase())}
              </a-tag>
            );
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(
                        this.$moment(record.date).format(
                          DATE_FORMAT.MOMENT_DAY_MONTH
                        )
                      )}
                    </span>
                    <small>
                      {this.$moment(record.date)
                        .format(DATE_FORMAT.MOMENT_TIME)
                        .toUpperCase()}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-number">
                      <span>{record.code}</span>
                    </div>
                    <div class="list-item-head-content-type">
                      <span>{record.type?.name}</span>
                    </div>
                    <div class="list-item-head-content-origin">
                      <span>{record.origin?.name}</span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    <div class="list-item-head-footer-amount">
                      <span>
                        {accounting.formatNumber(record.amount, 2, ",", ".")}
                      </span>
                    </div>
                    <div class="list-item-head-footer-status">
                      {record?.reject_reason && record?.status === "REJECTED"
                        ? tooltip(status)
                        : status}
                    </div>
                  </div>
                </div>
                {this.activeRow === index && (
                  <div class="list-item-actions">
                    <a-button-group>
                      <a-button
                        type="primary"
                        class="info-button"
                        title={this.labels.table.columns.action.moreInfo}
                        ghost
                        onClick={(event) =>
                          this.handleShowMoreInfo(record, event)
                        }
                        block
                      >
                        {this.labels.table.columns.action.moreInfo}
                      </a-button>
                      <router-link
                        class="view-button ant-btn ant-btn-primary ant-btn-block"
                        to={{
                          name: "transaction-view",
                          params: { code: record.code },
                        }}
                        title={this.labels.table.columns.action.view}
                      >
                        {this.labels.table.columns.action.view}
                      </router-link>
                      {record?.is_cancellable && <a-divider type="vertical" />}
                      {record?.is_cancellable && (
                        <a-button
                          onClick={() => this.confirmCancellation(record)}
                          type="danger"
                          class="cancel-button ant-btn ant-btn-danger"
                          block
                        >
                          {this.labels.table.columns.action.cancel}
                        </a-button>
                      )}
                    </a-button-group>
                  </div>
                )}
              </div>
            );
          },
        },
      ].filter((column) => !column?.hidden);
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.transaction-list {
  max-width: 100% !important;
  font-family: "Roboto", sans-serif;
  background-color: transparent;
  @media screen and (max-width: $screen-mobile) {
    max-width: 100% !important;
  }
  .content-base {
    padding-top: 60px;
    @media screen and (max-width: $screen-mobile) {
      max-width: 100% !important;
      padding-top: 14px;
    }
  }
  &-card {
    background-color: var(--card-shop-bg);
    border-color: var(--card-shop-shadow);
    border-radius: 8px;
    padding: 1.125rem;
    @media screen and (max-width: $screen-mobile) {
      padding: 2.125rem 1rem;
      width: 100%;
    }
    .ant-input {
      border: 1px solid var(--text-active) !important;
      color: var(--text) !important;
      font-family: "Exo 2", sans-serif !important;
      background-color: transparent;
    }
    .ant-input-search-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--text-active) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-input-clear-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--danger) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-table {
      &:hover {
        transition: 0.5s ease;
        background: unset;
      }
      tr.ant-table-row:hover {
        transition: 0.5s ease;
        background: var(--table-hover) !important;
      }
      tr:nth-child(2n) {
        transition: 0.5s ease;
        background: var(--table-striped) !important;
      }
      tr.table-row-disabled,
      tr.table-row-disabled:hover {
        color: var(--table-disabled) !important;
        background: var(--table-disabled) !important;
        font-weight: 600;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        transition: 0.5s ease;
        background: none !important;
      }
      &-tbody,
      &-thead {
        color: var(--text) !important;
        tr {
          th {
            color: var(--text) !important;
            background-color: transparent !important;
          }
        }
      }
      &-footer {
        background: transparent;
      }
      .ant-table-placeholder {
        background: var(--card-bg);
        color: var(--text);
        border-radius: 0;
      }
      @media screen and (max-width: $screen-mobile) {
        &-small {
          border: none;
        }
        &-body {
          margin: 0 1px;
        }
      }
    }
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
    }
    .ant-table-pagination.mini {
      .ant-pagination-prev .ant-pagination-item-link,
      .ant-pagination-next .ant-pagination-item-link {
        background-color: transparent !important;
        border-color: none !important;
        color: var(--text) !important;
      }
    }
    .ant-pagination-item {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
      a {
        color: var(--btn-text-secondary) !important;
      }
      &-active {
        color: var(--btn-text-primary) !important;
        background-color: var(--btn-bg-primary) !important;
        border-color: var(--btn-bc-primary) !important;
        a {
          color: var(--btn-text-primary) !important;
        }
      }
    }
    .ant-pagination {
      color: var(--text) !important;
    }
    .flex-direction-reverse {
      flex-direction: column-reverse;
    }
    .btn-new-transaction {
      color: var(--btn-text-primary);
      border-color: var(--btn-bc-primary);
      background-color: var(--btn-bg-primary);
      transition: all 0.5s ease;
      &:hover {
        transition: all 0.5s ease;
        color: var(--btn-text-primary);
        border-color: var(--btn-bc-primary);
        background-color: var(--btn-bg-primary);
      }
    }
    .action-button-view {
      color: var(--btn-text-secondary);
      border-color: var(--btn-bc-secondary);
      background-color: var(--btn-bg-secondary);
      transition: all 0.5s ease;
      &:hover {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
        transition: all 0.5s ease;
      }
    }
  }
  .text-danger {
    color: var(--danger) !important;
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 22%;
        border-right: 4px solid var(--text-muted);
        .success {
          color: var(--success);
        }
        .warning {
          color: var(--warning);
        }
        .danger {
          color: var(--danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 53%;
        overflow: hidden;
        &-number {
          span {
            font-size: 14px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
        &-type {
          span {
            font-size: 12px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
        }
        &-reference {
          span {
            font-size: 14px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
          &-tooltip {
            margin-left: 6px;
            .anticon {
              font-size: 18px;
            }
          }
        }
      }
      &-footer {
        max-width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        &-amount {
          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        &-balance {
          span {
            font-size: 12px;
            font-weight: 400;
          }
        }
      }
    }
    &-actions {
      .ant-btn-group {
        width: 100%;
      }
    }
  }
}
.more-info-modal {
  .ant-modal-content {
    background-color: var(--modal-bg);
  }
  .ant-modal-confirm-body {
    .ant-modal-confirm-title {
      color: var(--text-active);
    }
    .anticon {
      color: var(--text-active);
    }
    .ant-modal-confirm-content {
      color: var(--text);
    }
  }
  .ant-modal-confirm-btns {
    .ant-btn {
      color: var(--btn-text-secondary);
      border-color: var(--btn-bc-secondary);
      background-color: var(--btn-bg-secondary);
      &:active,
      &:focus,
      &:hover {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
      }
    }
  }
}

.ant-btn-group > .view-button {
  color: var(--btn-text-primary);
  border-color: var(--btn-bc-primary);
  background-color: var(--btn-bg-primary);
  transition: all 0.5s ease;
  &:hover {
    color: var(--btn-text-primary);
    border-color: var(--btn-bc-primary);
    background-color: var(--btn-bg-primary);
    transition: all 0.5s ease;
  }
}
.ant-btn-group > .info-button {
  color: var(--btn-text-secondary);
  border-color: var(--btn-bc-secondary);
  border-right-color: var(--btn-bc-secondary);
  background-color: var(--btn-bg-secondary);
  transition: all 0.5s ease;
  &:hover {
    color: var(--btn-text-secondary);
    border-color: var(--btn-bc-secondary);
    border-right-color: var(--btn-bc-secondary);
    background-color: var(--btn-bg-secondary);
    transition: all 0.5s ease;
  }
}

.requested-cancel-alert-modal {
  padding: 3.125rem;
  max-width: 481px;
  @media screen and (max-width: $screen-mobile) {
    padding: 2.125rem 1rem;
  }
  .ant-modal-content {
    background-color: var(--modal-bg);
  }
  .ant-modal-confirm-body {
    .ant-modal-confirm-title {
      color: var(--text-active);
    }
    .anticon {
      color: var(--text-active);
    }
    .ant-modal-confirm-content {
      color: var(--text);
    }
  }
  .ant-modal-title {
    font-family: "Exo 2", sans-serif;
    font-weight: 400;
    margin: 0;
    line-height: 1.3;
    word-break: break-word;
    font-size: 1.5rem;
    margin-bottom: 0;
    text-align: center;
  }
  .ant-modal-confirm-btns {
    .ant-btn {
      color: var(--btn-text-secondary);
      border-color: var(--btn-bc-secondary);
      background-color: var(--btn-bg-secondary);
      &:active,
      &:focus,
      &:hover {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
      }
      &-danger {
        color: var(--btn-text-primary);
        border-color: var(--btn-bc-primary);
        background-color: var(--btn-bg-primary);
        &:active,
        &:focus,
        &:hover {
          color: var(--btn-text-primary);
          border-color: var(--btn-bc-primary);
          background-color: var(--btn-bg-primary);
        }
      }
    }
  }
}
</style>
